import React, { useEffect, useState } from 'react';
import Layout from 'Component/Layouts/layout';
import {
  Box,
  Button,
  Chip,
  Select,
  MenuItem,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
} from '@mui/material';
import TableComponent from 'Component/Table';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAllAvaiableTraineesForApproval,
  submitTraineeStatus,
  updateTrainingTask,
} from 'thunks/trainingThunks/thunk';
import { updateBatchStatus, updateTraineeStatus } from 'reducer/traineeApprovalSlice';
import useCredentials from 'hooks/useCredentials';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

/**
 * Generic approval page for Product Financing training — handles both
 * L1 (step 1, per-trainee allow/decline) and L2 (step 2, batch approve/reject).
 * Route: /pf-approval/:batchId/:step
 */
const PFApproval = () => {
  const dispatch = useDispatch();
  const { batchId, step } = useParams();
  const stepNum = Number(step) || 1;
  const isL1 = stepNum === 1;

  const { userData: user } = useCredentials();
  const {
    data: { trainees, trainingType, submittedBy },
    loading,
  } = useSelector((state) => state.traineeApprovalSlice);

  const submittedByCurrentUser = submittedBy.includes(String(user?.employeeId));

  const [selectedActions, setSelectedActions] = useState({});
  const [batchStatus, setBatchStatus] = useState(null); // 'approved' | 'rejected'
  const [reason, setReason] = useState('');

  // L1: per-trainee decline modal
  const [openDeclineModal, setOpenDeclineModal] = useState(false);
  const [currentEmpId, setCurrentEmpId] = useState(null);
  const [declineReason, setDeclineReason] = useState('');

  // Batch-level confirm dialog (both L1 submit & L2 approve/reject)
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(''); // 'submit' | 'approve' | 'reject'

  useEffect(() => {
    const type = isL1 ? 'pf_l1' : 'pf_l2';
    dispatch(fetchAllAvaiableTraineesForApproval({ batchId, role: isL1 ? 'RM' : 'CH', type }));
  }, [batchId, dispatch, isL1]);

  // Pre-fill all trainees as "Allowed" on load (L1 default)
  useEffect(() => {
    if (isL1 && trainees?.length > 0 && !loading && Object.keys(selectedActions).length === 0) {
      const actions = {};
      trainees.forEach((t) => {
        actions[t.empId] = 'Allowed';
        dispatch(updateTraineeStatus({ empId: t.empId, allowed: true, reason: null }));
      });
      setSelectedActions(actions);
    }
  }, [trainees, loading, isL1]);

  // Mark task complete once submitted / already submitted by this user
  useEffect(() => {
    if ((batchStatus || submittedByCurrentUser) && user?.employeeId) {
      dispatch(
        updateTrainingTask({ approvalAuthority: Number(user.employeeId), batchId, step: stepNum })
      );
    }
  }, [batchStatus, submittedByCurrentUser, dispatch, user?.employeeId, batchId, stepNum]);

  // ── L1: per-trainee action ──────────────────────────────────────────────
  const handleActionChange = (empId, value) => {
    setSelectedActions((prev) => ({ ...prev, [empId]: value }));
    if (value === 'Declined') {
      setCurrentEmpId(empId);
      setOpenDeclineModal(true);
    } else {
      dispatch(updateTraineeStatus({ empId, allowed: true, reason: null }));
    }
  };

  const handleDeclineSubmit = () => {
    dispatch(
      updateTraineeStatus({
        empId: currentEmpId,
        allowed: false,
        reason: declineReason || 'No reason provided',
      })
    );
    setOpenDeclineModal(false);
    setDeclineReason('');
    setCurrentEmpId(null);
  };

  const handleAllowAll = () => {
    const actions = {};
    trainees.forEach((t) => {
      actions[t.empId] = 'Allowed';
      dispatch(updateTraineeStatus({ empId: t.empId, allowed: true, reason: null }));
    });
    setSelectedActions(actions);
  };

  // ── L1: submit ─────────────────────────────────────────────────────────
  const handleL1Submit = () => {
    const allSelected = trainees.every((t) => selectedActions[t.empId]);
    if (!allSelected) {
      toast.error('Please select an action for all trainees before submitting.', {
        theme: 'colored',
      });
      return;
    }
    setConfirmAction('submit');
    setOpenConfirmDialog(true);
  };

  const handleL1ConfirmSubmit = async () => {
    setOpenConfirmDialog(false);
    try {
      await dispatch(submitTraineeStatus({ batchId, step: stepNum, payload: trainees })).unwrap();
      toast.success('L1 approvals submitted successfully!', { theme: 'colored' });
      setBatchStatus('submitted');
    } catch {
      toast.error('Failed to submit approvals. Please try again.', { theme: 'colored' });
    }
  };

  // ── L2: batch approve/reject ────────────────────────────────────────────
  const handleBatchAction = (action) => {
    if (action === 'reject' && !reason.trim()) {
      toast.error('Please enter a reason for rejection.', { theme: 'colored' });
      return;
    }
    setConfirmAction(action);
    setOpenConfirmDialog(true);
  };

  const handleL2Confirm = async () => {
    setOpenConfirmDialog(false);
    const isApprove = confirmAction === 'approve';

    const updatedTrainees = isApprove
      ? trainees
      : trainees.map((t) => ({ ...t, allowed: false, reason }));

    try {
      await dispatch(
        submitTraineeStatus({
          batchId,
          step: stepNum,
          payload: {
            batchId,
            status: isApprove,
            reason: reason || null,
            trainees: updatedTrainees,
          },
        })
      ).unwrap();
      dispatch(updateBatchStatus({ batchId, status: isApprove }));
      setBatchStatus(isApprove ? 'approved' : 'rejected');
      toast.success(isApprove ? 'Batch approved successfully!' : 'Batch rejected.', {
        theme: 'colored',
      });
    } catch {
      toast.error('Failed to submit. Please try again.', { theme: 'colored' });
    }
  };

  const getRowStyle = (row) => {
    if (row.allowed === false || row.allowed === 'false') {
      return { backgroundColor: '#ffebee', '&:hover': { backgroundColor: '#ffcdd2' } };
    }
    return {};
  };

  const columns = [
    { label: 'Employee ID', field: 'empId' },
    { label: 'Employee Name', field: 'empName' },
    { label: 'Region', field: 'region' },
    { label: 'Branch', field: 'branch' },
    { label: 'Role', field: 'role' },
    { label: 'Nomination Count', field: 'nominationCount' },
    ...(isL1
      ? [
          {
            label: 'Action',
            field: 'allowed',
            render: (row) => (
              <Select
                value={selectedActions[row.empId] || ''}
                onChange={(e) => handleActionChange(row.empId, e.target.value)}
                displayEmpty
                size="small"
                sx={{ width: 120 }}
                disabled={submittedByCurrentUser || batchStatus !== null}
              >
                <MenuItem value="">Select</MenuItem>
                <MenuItem value="Allowed">Allowed</MenuItem>
                <MenuItem value="Declined">Declined</MenuItem>
              </Select>
            ),
          },
        ]
      : [
          {
            label: 'Status',
            field: 'allowed',
            render: (row) => (
              <Chip
                label={
                  row.allowed === true || row.allowed === 'true'
                    ? 'Allowed'
                    : row.allowed === false || row.allowed === 'false'
                      ? 'Declined'
                      : 'Pending L1'
                }
                color={
                  row.allowed === true || row.allowed === 'true'
                    ? 'success'
                    : row.allowed === false || row.allowed === 'false'
                      ? 'error'
                      : 'default'
                }
                size="small"
              />
            ),
          },
          { label: 'Reason', field: 'reason' },
        ]),
  ];

  const sortedTrainees = isL1
    ? trainees
    : [...trainees].sort((a, b) => {
        if (a.allowed === b.allowed) return 0;
        return a.allowed ? 1 : -1;
      });

  return (
    <Layout>
      <Box
        sx={{
          fontSize: '24px',
          fontWeight: 500,
          background: 'linear-gradient(to bottom, #E9FBE0 0%, #E6F0E8 100%)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 3,
        }}
      >
        <span>
          Product Financing — {isL1 ? 'L1 Approval' : 'L2 Approval'} &nbsp;
          <Typography component="span" variant="body2" color="text.secondary">
            Batch: {batchId}
          </Typography>
        </span>
        <Chip label={isL1 ? 'Level 1' : 'Level 2'} color="primary" variant="outlined" />
      </Box>

      {submittedByCurrentUser && (
        <Alert severity="info" sx={{ m: 2 }}>
          You have already submitted your approval for this batch.
        </Alert>
      )}
      {batchStatus && batchStatus !== 'submitted' && (
        <Alert severity={batchStatus === 'approved' ? 'success' : 'error'} sx={{ m: 2 }}>
          Batch has been {batchStatus}.
        </Alert>
      )}

      {/* L1 action buttons */}
      {isL1 && (
        <Box sx={{ display: 'flex', gap: 2, px: 3, py: 2 }}>
          <Button
            variant="outlined"
            onClick={handleAllowAll}
            disabled={submittedByCurrentUser || batchStatus !== null}
          >
            Allow All
          </Button>
          <Button
            variant="contained"
            onClick={handleL1Submit}
            disabled={submittedByCurrentUser || batchStatus !== null}
          >
            Submit L1 Approvals
          </Button>
        </Box>
      )}

      {/* L2 action buttons + rejection reason */}
      {!isL1 && (
        <Box sx={{ px: 3, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Rejection Reason (required if rejecting)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            size="small"
            multiline
            rows={2}
            fullWidth
            disabled={submittedByCurrentUser || batchStatus !== null}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color={batchStatus === 'approved' ? 'success' : 'primary'}
              onClick={() => handleBatchAction('approve')}
              disabled={submittedByCurrentUser || batchStatus !== null}
            >
              {batchStatus === 'approved' ? 'Approved' : 'Approve Batch'}
            </Button>
            <Button
              variant="contained"
              color={batchStatus === 'rejected' ? 'error' : 'primary'}
              onClick={() => handleBatchAction('reject')}
              disabled={submittedByCurrentUser || batchStatus !== null}
            >
              {batchStatus === 'rejected' ? 'Rejected' : 'Reject Batch'}
            </Button>
          </Box>
        </Box>
      )}

      <Box sx={{ px: 2, pb: 4 }}>
        {loading ? (
          <p>Loading...</p>
        ) : (
          <TableComponent
            tableData={sortedTrainees}
            columns={columns}
            tableName="PFTraineeList"
            count={sortedTrainees?.length}
            useWholeRowClicked={false}
            getRowStyle={getRowStyle}
            enableLocalSearch
          />
        )}
      </Box>

      {/* L1: per-trainee decline reason modal */}
      <Dialog
        open={openDeclineModal}
        onClose={() => {
          setOpenDeclineModal(false);
          setDeclineReason('');
        }}
      >
        <DialogTitle>Reason for Declining</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            sx={{ mt: 1 }}
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenDeclineModal(false);
              setDeclineReason('');
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleDeclineSubmit} disabled={!declineReason.trim()}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation dialog */}
      <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)}>
        <DialogTitle>
          {confirmAction === 'submit'
            ? 'Confirm L1 Submission'
            : confirmAction === 'approve'
              ? 'Approve Batch'
              : 'Reject Batch'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmAction === 'submit'
              ? 'Are you sure you want to submit your L1 approval decisions for all trainees?'
              : confirmAction === 'approve'
                ? 'Are you sure you want to approve this batch? This will move to L2 approval.'
                : `Are you sure you want to reject this batch? Reason: "${reason}"`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDialog(false)}>Cancel</Button>
          <Button onClick={isL1 ? handleL1ConfirmSubmit : handleL2Confirm} autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default PFApproval;
